import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import {
  getTtadminData,
  type AdminUser,
  type AdminSession,
  type AdminSubscription,
  type AdminCollaboration,
  type AdminPublishedSite,
  type AdminPartner,
} from '../endpoints/admin/ttadmin_GET.schema';
import { postTtadminAction, type InputType } from '../endpoints/admin/ttadmin_POST.schema';
import { useAuth } from '../helpers/useAuth';
import { toast } from 'sonner';
import { Spinner } from '../components/Spinner';
import {
  Users,
  FolderOpen,
  CreditCard,
  UserPlus,
  RefreshCw,
  Search,
  Trash2,
  Archive,
  UserMinus,
  AlertCircle,
  Globe,
  ExternalLink,
  XCircle,
  Server,
  Handshake,
  CheckCircle2,
  XOctagon,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button } from '../components/Button';
import styles from './ttadmin.module.css';

const ADMIN_QUERY_KEY = ['admin', 'ttadmin'] as const;

type TabId = 'users' | 'sessions' | 'sites' | 'published' | 'subscriptions' | 'collaborations' | 'partners';

export default function TtadminPage() {
  const { authState } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('users');
  const [search, setSearch] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ADMIN_QUERY_KEY,
    queryFn: () => getTtadminData(),
    staleTime: 60_000,
  });

  const actionMutation = useMutation({
    mutationFn: (input: InputType) => postTtadminAction(input),
    onSuccess: (result) => {
      toast.success(result.message);
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const handleAction = (input: InputType) => {
    actionMutation.mutate(input);
  };

  // Only ryand32@gmail.com can access this page
  const SUPERADMIN_EMAIL = 'ryand32@gmail.com';

  if (authState.type === 'loading' || isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <Spinner />
          <span>Loading admin panel...</span>
        </div>
      </div>
    );
  }

  if (authState.type === 'authenticated' && authState.user.email !== SUPERADMIN_EMAIL) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>
          <AlertCircle size={32} />
          <span>Access denied. This page is restricted.</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>
          <AlertCircle size={32} />
          <span>{error instanceof Error ? error.message : 'Failed to load'}</span>
          <Button variant="outline" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const publishedCount = data.sessions.filter((s) => s.previewSubdomain).length;

  const tabs: { id: TabId; label: string; icon: React.ReactNode; count: number }[] = [
    { id: 'users', label: 'Users', icon: <Users size={15} />, count: data.stats.totalUsers },
    {
      id: 'sessions',
      label: 'Sessions',
      icon: <FolderOpen size={15} />,
      count: data.stats.totalSessions,
    },
    { id: 'sites', label: 'Sites', icon: <Server size={15} />, count: data.stats.totalSites },
    { id: 'published', label: 'Published', icon: <Globe size={15} />, count: publishedCount },
    {
      id: 'subscriptions',
      label: 'Subscriptions',
      icon: <CreditCard size={15} />,
      count: data.stats.totalSubscriptions,
    },
    {
      id: 'collaborations',
      label: 'Collaborations',
      icon: <UserPlus size={15} />,
      count: data.collaborations.length,
    },
    {
      id: 'partners',
      label: 'Partners',
      icon: <Handshake size={15} />,
      count: data.partners?.length || 0,
    },
  ];

  return (
    <div className={styles.page}>
      <>
        <title>TT Admin - Tasking.tech</title>
        <meta name="robots" content="noindex, nofollow" />
      </>

      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <img src="/superadmin-avatar.png" alt="Superadmin" className={styles.adminAvatar} />
            <h1 className={styles.title}>ttadmin</h1>
            <span className={styles.adminBadge}>SUPERADMIN</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className={styles.refreshBtn}
            onClick={() => refetch()}
          >
            <RefreshCw size={14} />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className={styles.statsGrid}>
          <StatCard label="Total Users" value={data.stats.totalUsers} />
          <StatCard label="Total Sessions" value={data.stats.totalSessions} />
          <StatCard label="Active Sessions" value={data.stats.activeSessions} />
          <StatCard label="CF Sites" value={data.stats.totalSites} />
          <StatCard label="Published" value={publishedCount} />
          <StatCard label="Subscriptions" value={data.stats.totalSubscriptions} />
          <StatCard label="Active Subs" value={data.stats.activeSubscriptions} />
          <StatCard label="Trials" value={data.stats.trialSubscriptions} />
          <StatCard label="Partners" value={data.stats.totalPartners || 0} />
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
              onClick={() => {
                setActiveTab(tab.id);
                setSearch('');
              }}
            >
              {tab.icon}
              {tab.label}
              <span className={styles.tabCount}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Table Content */}
        <div className={styles.tableWrapper}>
          <div className={styles.searchRow}>
            <Search size={16} style={{ color: 'var(--muted-foreground)' }} />
            <input
              className={styles.searchInput}
              placeholder={`Search ${activeTab}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className={styles.tableScroll}>
            {activeTab === 'users' && (
              <UsersTable
                users={data.users}
                search={search}
                onAction={handleAction}
                isPending={actionMutation.isPending}
              />
            )}
            {activeTab === 'sessions' && (
              <SessionsTable
                sessions={data.sessions}
                search={search}
                onAction={handleAction}
                isPending={actionMutation.isPending}
              />
            )}
            {activeTab === 'sites' && (
              <SitesTable
                sites={data.sites}
                search={search}
                onAction={handleAction}
                isPending={actionMutation.isPending}
              />
            )}
            {activeTab === 'published' && (
              <PublishedTable
                sessions={data.sessions}
                search={search}
                onAction={handleAction}
                isPending={actionMutation.isPending}
              />
            )}
            {activeTab === 'subscriptions' && (
              <SubscriptionsTable
                subscriptions={data.subscriptions}
                search={search}
                onAction={handleAction}
                isPending={actionMutation.isPending}
              />
            )}
            {activeTab === 'collaborations' && (
              <CollaborationsTable
                collaborations={data.collaborations}
                search={search}
                onAction={handleAction}
                isPending={actionMutation.isPending}
              />
            )}
            {activeTab === 'partners' && (
              <PartnersTable
                partners={data.partners || []}
                search={search}
                onAction={handleAction}
                isPending={actionMutation.isPending}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className={styles.statCard}>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statValue}>{value}</span>
    </div>
  );
}

function formatDate(d: Date | string | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function roleBadgeClass(role: string): string {
  if (role === 'admin') return styles.badgeAdmin;
  if (role === 'host') return styles.badgeHost;
  return styles.badgeGuest;
}

function statusBadgeClass(status: string): string {
  if (status === 'active' || status === 'deployed') return styles.badgeActive;
  if (status === 'archived') return styles.badgeArchived;
  if (status === 'trialing') return styles.badgeTrialing;
  if (status === 'canceled' || status === 'past_due') return styles.badgeCanceled;
  return styles.badgeDraft;
}

function tierBadgeClass(tier: string): string {
  if (tier === 'basic') return styles.badgeBasic;
  if (tier === 'pro') return styles.badgePro;
  if (tier === 'enterprise') return styles.badgeEnterprise;
  return styles.badgeFree;
}

/* Users Table */
function UsersTable({
  users,
  search,
  onAction,
  isPending,
}: {
  users: AdminUser[];
  search: string;
  onAction: (input: InputType) => void;
  isPending: boolean;
}) {
  const filtered = useMemo(() => {
    if (!search) return users;
    const s = search.toLowerCase();
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(s) ||
        u.displayName.toLowerCase().includes(s) ||
        u.role.toLowerCase().includes(s),
    );
  }, [users, search]);

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>ID</th>
          <th>Email</th>
          <th>Display Name</th>
          <th>Role</th>
          <th>Sessions</th>
          <th>Subscription</th>
          <th>Created</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {filtered.map((u) => (
          <tr key={u.id}>
            <td className={styles.mono}>{u.id}</td>
            <td>{u.email}</td>
            <td>{u.displayName}</td>
            <td>
              <span className={`${styles.badge} ${roleBadgeClass(u.role)}`}>{u.role}</span>
            </td>
            <td className={styles.mono}>{u.sessionCount}</td>
            <td>
              {u.subscriptionTier ? (
                <span className={`${styles.badge} ${tierBadgeClass(u.subscriptionTier)}`}>
                  {u.subscriptionTier}
                </span>
              ) : (
                <span style={{ color: 'var(--muted-foreground)', fontSize: '0.75rem' }}>none</span>
              )}
            </td>
            <td>{formatDate(u.createdAt)}</td>
            <td>
              <div className={styles.actions}>
                <select
                  className={styles.actionBtn}
                  value={u.role}
                  disabled={isPending}
                  onChange={(e) =>
                    onAction({
                      action: 'updateUserRole',
                      userId: u.id,
                      role: e.target.value as 'admin' | 'host' | 'guest',
                    })
                  }
                >
                  <option value="admin">admin</option>
                  <option value="host">host</option>
                  <option value="guest">guest</option>
                </select>
              </div>
            </td>
          </tr>
        ))}
        {filtered.length === 0 && (
          <tr>
            <td
              colSpan={8}
              style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted-foreground)' }}
            >
              No users found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/* Sessions Table */
function SessionsTable({
  sessions,
  search,
  onAction,
  isPending,
}: {
  sessions: AdminSession[];
  search: string;
  onAction: (input: InputType) => void;
  isPending: boolean;
}) {
  const filtered = useMemo(() => {
    if (!search) return sessions;
    const s = search.toLowerCase();
    return sessions.filter(
      (sess) =>
        sess.name.toLowerCase().includes(s) ||
        sess.id.toLowerCase().includes(s) ||
        (sess.ownerEmail || '').toLowerCase().includes(s) ||
        (sess.ownerDisplayName || '').toLowerCase().includes(s),
    );
  }, [sessions, search]);

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>ID</th>
          <th>Name</th>
          <th>Owner</th>
          <th>Status</th>
          <th>Template</th>
          <th>Published</th>
          <th>Files</th>
          <th>Participants</th>
          <th>Updated</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {filtered.map((s) => (
          <tr key={s.id}>
            <td className={styles.mono} title={s.id}>
              {s.id.slice(0, 8)}...
            </td>
            <td title={s.name}>{s.name}</td>
            <td>{s.ownerEmail || s.ownerDisplayName || '—'}</td>
            <td>
              <span className={`${styles.badge} ${statusBadgeClass(s.status)}`}>{s.status}</span>
            </td>
            <td className={styles.mono}>{s.templateId || '—'}</td>
            <td>
              {s.previewSubdomain ? (
                <a
                  href={`https://${s.previewSubdomain}.tasking.tech`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.publishedLink}
                  title={`${s.previewSubdomain}.tasking.tech`}
                >
                  <Globe size={12} />
                  {s.previewSubdomain.slice(0, 10)}...
                </a>
              ) : (
                <span style={{ color: 'var(--muted-foreground)', fontSize: '0.7rem' }}>—</span>
              )}
            </td>
            <td className={styles.mono}>{s.fileCount}</td>
            <td className={styles.mono}>{s.participantCount}</td>
            <td>{formatDate(s.updatedAt)}</td>
            <td>
              <div className={styles.actions}>
                {s.status !== 'archived' && (
                  <button
                    className={styles.actionBtn}
                    disabled={isPending}
                    title="Archive session"
                    onClick={() => onAction({ action: 'archiveSession', sessionId: s.id })}
                  >
                    <Archive size={12} />
                  </button>
                )}
                <button
                  className={styles.actionBtnDanger}
                  disabled={isPending}
                  title="Delete session"
                  onClick={() => {
                    if (window.confirm(`Delete session "${s.name}"? This cannot be undone.`)) {
                      onAction({ action: 'deleteSession', sessionId: s.id });
                    }
                  }}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </td>
          </tr>
        ))}
        {filtered.length === 0 && (
          <tr>
            <td
              colSpan={10}
              style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted-foreground)' }}
            >
              No sessions found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/* Subscriptions Table */
function SubscriptionsTable({
  subscriptions,
  search,
  onAction,
  isPending,
}: {
  subscriptions: AdminSubscription[];
  search: string;
  onAction: (input: InputType) => void;
  isPending: boolean;
}) {
  const filtered = useMemo(() => {
    if (!search) return subscriptions;
    const s = search.toLowerCase();
    return subscriptions.filter(
      (sub) =>
        sub.userEmail.toLowerCase().includes(s) ||
        sub.userDisplayName.toLowerCase().includes(s) ||
        sub.tier.toLowerCase().includes(s) ||
        sub.status.toLowerCase().includes(s),
    );
  }, [subscriptions, search]);

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>User</th>
          <th>Email</th>
          <th>Tier</th>
          <th>Status</th>
          <th>Stripe Customer</th>
          <th>Period End</th>
          <th>Trial End</th>
          <th>Cancel?</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {filtered.map((s) => (
          <tr key={s.id}>
            <td>{s.userDisplayName}</td>
            <td>{s.userEmail}</td>
            <td>
              <span className={`${styles.badge} ${tierBadgeClass(s.tier)}`}>{s.tier}</span>
            </td>
            <td>
              <span className={`${styles.badge} ${statusBadgeClass(s.status)}`}>{s.status}</span>
            </td>
            <td className={styles.mono}>
              {s.stripeCustomerId ? s.stripeCustomerId.slice(0, 12) + '...' : '—'}
            </td>
            <td>{s.currentPeriodEnd || '—'}</td>
            <td>{s.trialEnd || '—'}</td>
            <td>{s.cancelAtPeriodEnd ? 'Yes' : 'No'}</td>
            <td>
              <div className={styles.actions}>
                <select
                  className={styles.actionBtn}
                  value={s.tier}
                  disabled={isPending}
                  onChange={(e) =>
                    onAction({
                      action: 'updateSubscription',
                      userId: s.userId,
                      tier: e.target.value as 'free' | 'basic' | 'pro' | 'enterprise',
                      status: s.status as
                        | 'active'
                        | 'canceled'
                        | 'past_due'
                        | 'inactive'
                        | 'trialing',
                    })
                  }
                >
                  <option value="free">free</option>
                  <option value="basic">basic</option>
                  <option value="pro">pro</option>
                  <option value="enterprise">enterprise</option>
                </select>
              </div>
            </td>
          </tr>
        ))}
        {filtered.length === 0 && (
          <tr>
            <td
              colSpan={9}
              style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted-foreground)' }}
            >
              No subscriptions found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/* Collaborations Table */
function CollaborationsTable({
  collaborations,
  search,
  onAction,
  isPending,
}: {
  collaborations: AdminCollaboration[];
  search: string;
  onAction: (input: InputType) => void;
  isPending: boolean;
}) {
  const filtered = useMemo(() => {
    if (!search) return collaborations;
    const s = search.toLowerCase();
    return collaborations.filter(
      (c) =>
        c.userEmail.toLowerCase().includes(s) ||
        c.userDisplayName.toLowerCase().includes(s) ||
        c.sessionName.toLowerCase().includes(s) ||
        c.sessionId.toLowerCase().includes(s),
    );
  }, [collaborations, search]);

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Session</th>
          <th>Session ID</th>
          <th>User</th>
          <th>Email</th>
          <th>Permission</th>
          <th>Joined</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {filtered.map((c) => (
          <tr key={c.id}>
            <td title={c.sessionName}>{c.sessionName}</td>
            <td className={styles.mono} title={c.sessionId}>
              {c.sessionId.slice(0, 8)}...
            </td>
            <td>{c.userDisplayName}</td>
            <td>{c.userEmail}</td>
            <td>
              <span
                className={`${styles.badge} ${
                  c.permissionLevel === 'edit' ? styles.badgeEdit : styles.badgeView
                }`}
              >
                {c.permissionLevel}
              </span>
            </td>
            <td>{formatDate(c.joinedAt)}</td>
            <td>
              <div className={styles.actions}>
                <button
                  className={styles.actionBtnDanger}
                  disabled={isPending}
                  title="Remove participant"
                  onClick={() =>
                    onAction({
                      action: 'removeParticipant',
                      sessionId: c.sessionId,
                      userId: c.userId,
                    })
                  }
                >
                  <UserMinus size={12} />
                </button>
              </div>
            </td>
          </tr>
        ))}
        {filtered.length === 0 && (
          <tr>
            <td
              colSpan={7}
              style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted-foreground)' }}
            >
              No collaborations found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/* Sites Table (CF Pages published sites from publishedSites DB table) */
function SitesTable({
  sites,
  search,
  onAction,
  isPending,
}: {
  sites: AdminPublishedSite[];
  search: string;
  onAction: (input: InputType) => void;
  isPending: boolean;
}) {
  const filtered = useMemo(() => {
    if (!search) return sites;
    const s = search.toLowerCase();
    return sites.filter(
      (site) =>
        site.subdomain.toLowerCase().includes(s) ||
        site.deploymentUrl.toLowerCase().includes(s) ||
        site.sessionId.toLowerCase().includes(s) ||
        (site.ownerEmail || '').toLowerCase().includes(s) ||
        (site.ownerDisplayName || '').toLowerCase().includes(s),
    );
  }, [sites, search]);

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>ID</th>
          <th>Subdomain</th>
          <th>Deployment URL</th>
          <th>Session ID</th>
          <th>Owner</th>
          <th>Status</th>
          <th>Last Deployed</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {filtered.map((site) => (
          <tr key={site.id}>
            <td className={styles.mono}>{site.id}</td>
            <td className={styles.mono}>{site.subdomain}</td>
            <td>
              <a
                href={`https://${site.subdomain}.tasking.tech`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.publishedLink}
              >
                <ExternalLink size={12} />
                {site.subdomain}.tasking.tech
              </a>
            </td>
            <td className={styles.mono} title={site.sessionId}>
              {site.sessionId.slice(0, 8)}...
            </td>
            <td>{site.ownerEmail || site.ownerDisplayName || '—'}</td>
            <td>
              <span className={`${styles.badge} ${statusBadgeClass(site.deploymentStatus)}`}>
                {site.deploymentStatus}
              </span>
            </td>
            <td>{formatDate(site.lastDeployedAt)}</td>
            <td>
              <div className={styles.actions}>
                <button
                  className={styles.actionBtnDanger}
                  disabled={isPending}
                  title="Delete site from CF Pages + DB"
                  onClick={() => {
                    if (
                      window.confirm(
                        `Delete site "${site.subdomain}.tasking.tech"? This will remove the CF Pages project and DB record.`,
                      )
                    ) {
                      onAction({
                        action: 'deleteSite',
                        siteId: site.id,
                        cloudflareProjectName: site.cloudflareProjectName,
                      });
                    }
                  }}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </td>
          </tr>
        ))}
        {filtered.length === 0 && (
          <tr>
            <td
              colSpan={8}
              style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted-foreground)' }}
            >
              No published sites found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/* Published URLs Table */
function PublishedTable({
  sessions,
  search,
  onAction,
  isPending,
}: {
  sessions: AdminSession[];
  search: string;
  onAction: (input: InputType) => void;
  isPending: boolean;
}) {
  const filtered = useMemo(() => {
    const published = sessions.filter((s) => s.previewSubdomain);
    if (!search) return published;
    const s = search.toLowerCase();
    return published.filter(
      (sess) =>
        sess.name.toLowerCase().includes(s) ||
        sess.id.toLowerCase().includes(s) ||
        (sess.previewSubdomain || '').toLowerCase().includes(s) ||
        (sess.ownerEmail || '').toLowerCase().includes(s),
    );
  }, [sessions, search]);

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Session</th>
          <th>Session ID</th>
          <th>Owner</th>
          <th>Published URL</th>
          <th>Status</th>
          <th>Updated</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {filtered.map((s) => (
          <tr key={s.id}>
            <td title={s.name}>{s.name}</td>
            <td className={styles.mono} title={s.id}>
              {s.id.slice(0, 8)}...
            </td>
            <td>{s.ownerEmail || s.ownerDisplayName || '—'}</td>
            <td>
              <a
                href={`https://${s.previewSubdomain}.tasking.tech`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.publishedLink}
              >
                <ExternalLink size={12} />
                {s.previewSubdomain}.tasking.tech
              </a>
            </td>
            <td>
              <span className={`${styles.badge} ${statusBadgeClass(s.status)}`}>{s.status}</span>
            </td>
            <td>{formatDate(s.updatedAt)}</td>
            <td>
              <div className={styles.actions}>
                <button
                  className={styles.actionBtnDanger}
                  disabled={isPending}
                  title="Remove published URL"
                  onClick={() => {
                    if (
                      window.confirm(`Unpublish "${s.name}"? This will remove the preview URL.`)
                    ) {
                      onAction({ action: 'clearPublishedUrl', sessionId: s.id });
                    }
                  }}
                >
                  <XCircle size={12} />
                </button>
              </div>
            </td>
          </tr>
        ))}
        {filtered.length === 0 && (
          <tr>
            <td
              colSpan={7}
              style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted-foreground)' }}
            >
              No published sessions found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/* Partners Table */
function PartnersTable({
  partners,
  search,
  onAction,
  isPending,
}: {
  partners: AdminPartner[];
  search: string;
  onAction: (input: InputType) => void;
  isPending: boolean;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [denyReasons, setDenyReasons] = useState<Record<string, string>>({});

  const filtered = useMemo(() => {
    if (!search) return partners;
    const s = search.toLowerCase();
    return partners.filter(
      (p) =>
        p.name.toLowerCase().includes(s) ||
        p.contactEmail.toLowerCase().includes(s) ||
        p.slug.toLowerCase().includes(s) ||
        p.partnerTier.toLowerCase().includes(s),
    );
  }, [partners, search]);

  const getStatus = (p: AdminPartner): string => {
    return p.metadata?.applicationStatus || (p.isActive ? 'approved' : 'pending');
  };

  const statusBadge = (status: string) => {
    if (status === 'approved') return { background: '#16a34a22', color: '#22c55e', border: '1px solid #22c55e44' };
    if (status === 'denied') return { background: '#ef444422', color: '#ef4444', border: '1px solid #ef444444' };
    return { background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b44' };
  };

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Company Name</th>
          <th>Contact Email</th>
          <th>Tier</th>
          <th>Status</th>
          <th>Applied Date</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {filtered.map((p) => {
          const status = getStatus(p);
          const badge = statusBadge(status);
          const isExpanded = expandedId === p.id;
          return (
            <React.Fragment key={p.id}>
              <tr>
                <td>
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : p.id)}
                    style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem', padding: 0, font: 'inherit' }}
                  >
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    {p.name}
                  </button>
                </td>
                <td>{p.contactEmail}</td>
                <td>
                  <span className={`${styles.badge}`} style={{ textTransform: 'capitalize' }}>{p.partnerTier}</span>
                </td>
                <td>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '0.15rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      ...badge,
                    }}
                  >
                    {status}
                  </span>
                </td>
                <td>{formatDate(p.metadata?.appliedAt || p.createdAt)}</td>
                <td>
                  <div className={styles.actions}>
                    {status === 'pending' && (
                      <>
                        <button
                          className={styles.actionBtn}
                          disabled={isPending}
                          title="Approve partner"
                          style={{ color: '#22c55e' }}
                          onClick={() => {
                            if (window.confirm(`Approve partner "${p.name}"?`)) {
                              onAction({ action: 'approvePartner', partnerId: p.id });
                            }
                          }}
                        >
                          <CheckCircle2 size={14} />
                        </button>
                        <button
                          className={styles.actionBtnDanger}
                          disabled={isPending}
                          title="Deny partner"
                          onClick={() => {
                            const reason = denyReasons[p.id]?.trim();
                            if (!reason) {
                              setExpandedId(p.id);
                              return;
                            }
                            if (window.confirm(`Deny partner "${p.name}"?\nReason: ${reason}`)) {
                              onAction({ action: 'denyPartner', partnerId: p.id, reason });
                            }
                          }}
                        >
                          <XOctagon size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
              {isExpanded && (
                <tr>
                  <td colSpan={6} style={{ padding: '1rem 1.5rem', background: 'rgba(255,255,255,0.02)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 2rem', fontSize: '0.8rem' }}>
                      <div><strong style={{ color: 'var(--muted-foreground)' }}>Website:</strong> {p.metadata?.website || '—'}</div>
                      <div><strong style={{ color: 'var(--muted-foreground)' }}>Slug:</strong> <code>{p.slug}</code></div>
                      <div style={{ gridColumn: '1 / -1' }}><strong style={{ color: 'var(--muted-foreground)' }}>Description:</strong> {p.metadata?.description || '—'}</div>
                      <div style={{ gridColumn: '1 / -1' }}><strong style={{ color: 'var(--muted-foreground)' }}>Why Resell:</strong> {p.metadata?.whyResell || '—'}</div>
                      <div style={{ gridColumn: '1 / -1' }}><strong style={{ color: 'var(--muted-foreground)' }}>Why Tasking:</strong> {p.metadata?.whyTasking || '—'}</div>
                      <div><strong style={{ color: 'var(--muted-foreground)' }}>Signer Name:</strong> {p.metadata?.signerName || '—'}</div>
                      <div><strong style={{ color: 'var(--muted-foreground)' }}>Signer Title:</strong> {p.metadata?.signerTitle || '—'}</div>
                      <div><strong style={{ color: 'var(--muted-foreground)' }}>Signer Company:</strong> {p.metadata?.signerCompanyName || '—'}</div>
                      <div><strong style={{ color: 'var(--muted-foreground)' }}>Billing Email:</strong> {p.billingEmail || '—'}</div>
                      {p.metadata?.signatureImage && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <strong style={{ color: 'var(--muted-foreground)' }}>Signature:</strong>
                          <div style={{ marginTop: '0.5rem' }}>
                            <img
                              src={p.metadata.signatureImage}
                              alt="Signature"
                              style={{ maxHeight: 60, background: '#fff', border: '1px solid #ccc', borderRadius: 4, padding: 4 }}
                            />
                          </div>
                        </div>
                      )}
                      {p.metadata?.applicationStatus === 'denied' && p.metadata?.denialReason && (
                        <div style={{ gridColumn: '1 / -1', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '0.5rem 0.75rem' }}>
                          <strong style={{ color: '#ef4444' }}>Denial Reason:</strong> {p.metadata.denialReason}
                        </div>
                      )}
                      {status === 'pending' && (
                        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                          <input
                            type="text"
                            placeholder="Denial reason (required to deny)..."
                            value={denyReasons[p.id] || ''}
                            onChange={(e) => setDenyReasons((prev) => ({ ...prev, [p.id]: e.target.value }))}
                            style={{
                              flex: 1,
                              padding: '0.4rem 0.6rem',
                              borderRadius: 6,
                              border: '1px solid rgba(255,255,255,0.12)',
                              background: 'rgba(255,255,255,0.05)',
                              color: 'var(--foreground)',
                              fontSize: '0.8rem',
                            }}
                          />
                          <button
                            className={styles.actionBtn}
                            disabled={isPending}
                            style={{ color: '#22c55e', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                            onClick={() => {
                              if (window.confirm(`Approve partner "${p.name}"?`)) {
                                onAction({ action: 'approvePartner', partnerId: p.id });
                              }
                            }}
                          >
                            <CheckCircle2 size={14} /> Approve
                          </button>
                          <button
                            className={styles.actionBtnDanger}
                            disabled={isPending || !denyReasons[p.id]?.trim()}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                            onClick={() => {
                              const reason = denyReasons[p.id]?.trim();
                              if (reason && window.confirm(`Deny partner "${p.name}"?\nReason: ${reason}`)) {
                                onAction({ action: 'denyPartner', partnerId: p.id, reason });
                              }
                            }}
                          >
                            <XOctagon size={14} /> Deny
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
        {filtered.length === 0 && (
          <tr>
            <td
              colSpan={6}
              style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted-foreground)' }}
            >
              No partners found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
